import { devError } from '../../../../../utils/devLog';
/**
 * Data Utilities for SharedDataContext
 * Helper functions for data manipulation and validation
 */

/**
 * Normalize a data path
 */
export function normalizePath(path: string): string {
    return path.trim().replace(/\/+/g, '.').replace(/^\.+|\.+$/g, '');
}

/**
 * Deep equality check for data values
 */
export function deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    if (typeof a === 'object') {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        
        if (keysA.length !== keysB.length) return false;
        
        for (const key of keysA) {
            if (!keysB.includes(key)) return false;
            if (!deepEqual(a[key], b[key])) return false;
        }
        
        return true;
    }

    return false;
}

/**
 * Check if a path matches a pattern (supports wildcards)
 */
export function pathMatches(actualPath: string, subscriptionPath: string): boolean {
    // Simple wildcard matching (* and **)
    if (subscriptionPath.includes('*')) {
        const pattern = subscriptionPath
            .replace(/\*\*/g, '.*')  // ** matches any number of path segments
            .replace(/\*/g, '[^.]*'); // * matches single path segment
        
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(actualPath);
    }

    return false;
}

/**
 * Create a deep clone of an object
 */
export function deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime()) as unknown as T;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item)) as unknown as T;
    }

    const cloned = {} as T;
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            cloned[key] = deepClone(obj[key]);
        }
    }

    return cloned;
}

/**
 * Get nested value from object by path
 */
export function getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
}

/**
 * Set nested value in object by path
 */
export function setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop();
    
    if (!lastKey) return;

    const target = keys.reduce((current, key) => {
        if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
        }
        return current[key];
    }, obj);

    target[lastKey] = value;
}

/**
 * Validate data against a schema
 */
export function validateData(value: any, path: string, validator?: (value: any, path: string) => boolean | string): boolean {
    if (!validator) return true;

    try {
        const result = validator(value, path);
        return result === true;
    } catch (error) {
        devError(`[DataUtils] Validation error at ${path}:`, error);
        return false;
    }
}

/**
 * Transform data using a transform function
 */
export function transformData<T, R>(value: T, path: string, transform?: (value: T, path: string) => R): R | T {
    if (!transform) return value;

    try {
        return transform(value, path);
    } catch (error) {
        devError(`[DataUtils] Transform error at ${path}:`, error);
        return value;
    }
}
